import React, { useImperativeHandle, useMemo, useRef, useState } from "react";

import { FASelectInput } from "@impero-com/common/components/form/formAtomFields/faSelectInput";
import * as Cmp from "@impero-com/common/utils/comparison";
import { formAtom } from "@impero-com/common/utils/form/formAtom";
import {
    useCompoundFormState,
    useFormAtom,
} from "@impero-com/common/utils/form/formAtom/hooks";
import { FormAtom } from "@impero-com/common/utils/form/formAtom/types";
import { t } from "@impero-com/common/utils/i18n";
import {
    FieldError,
    FormBox,
    FormField,
    FormLabel,
    Tooltip,
    Svg,
    TST_COLUMN_SIZE,
    Button,
    Modal,
    TSTTable,
} from "@impero-com/ui";
import {
    createColumnHelper,
    getCoreRowModel,
    useReactTable,
} from "@tanstack/react-table";
import { Radio } from "antd";
import { atom, SetStateAction, useAtom } from "jotai";
import { focusAtom } from "jotai-optics";
import { isEmpty, isEqual } from "lodash-es";
import * as Yup from "yup";

import {
    DataSheetCellValue,
    DataSheetColumnView,
    DataSheetGroupingAndControlsAssigned,
    DataSheetStructure,
    ControlOverview,
} from "api/generated/model";
import alertTriangle from "img/alert-triangle.svg";

import {
    DataSheetAssignmentsForControlProgramAndCurrentControl,
    DataSheetGroupingAndControlsAssignedForCurrentControl,
} from "../../org/control/view/components/tasks/types";

import { CompoundFormForwardedRef } from "./fieldBuilder";
import * as theme from "./theme.css";

interface DataSheetFieldTypeDetails {
    dataSheetId: string;
    grouping: (DataSheetCellValue | null)[];
}

interface DataSheetEditorProps<FT extends DataSheetFieldTypeDetails> {
    fieldId: string;
    atom: FormAtom<FT>;
    dataSheetAssignments: DataSheetAssignmentsForControlProgramAndCurrentControl;
    controlOverrideId: string;
    controlOverrideTitle: string;
    disabled?: boolean;
}

function useFieldTypeAtomDataSheetAndGrouping<
    FT extends DataSheetFieldTypeDetails,
>(
    fieldTypeAtom: FormAtom<FT>,
    dataSheetAssignments: DataSheetAssignmentsForControlProgramAndCurrentControl
) {
    return useMemo(() => {
        const dataSheetAtom: FormAtom<string> = formAtom(
            atom(
                (get) => get(fieldTypeAtom).dataSheetId,
                (_get, set, update: SetStateAction<string>) => {
                    return set(fieldTypeAtom, (fieldType) => {
                        // Clone
                        const newFieldType: FT = {
                            ...fieldType,
                        };
                        // Regular update
                        newFieldType.dataSheetId =
                            update instanceof Function
                                ? update(fieldType.dataSheetId)
                                : update;
                        // Reset the grouping as it has just become invalid.
                        newFieldType.grouping = [];
                        return newFieldType;
                    });
                }
            ),
            Yup.string()
                .defined()
                .test({
                    name: "dataSheetExists",
                    test: (value, { createError }) => {
                        const PATH = "dataSheetId";
                        if (
                            !dataSheetAssignments.content.some(
                                (ds) => ds.resource.dataSheetId === value
                            )
                        ) {
                            return createError({
                                path: PATH,
                                message: t(
                                    "FieldBuilder_Error_DataSheet_Required"
                                ),
                            });
                        } else {
                            return true;
                        }
                    },
                })
        );

        const groupingAtom: FormAtom<(DataSheetCellValue | null)[]> = formAtom(
            focusAtom(fieldTypeAtom, (fieldType) => fieldType.prop("grouping")),
            Yup.array()
                .defined()
                .test({
                    name: "groupingExists",
                    test: (value, { createError }) => {
                        const PATH = "grouping";
                        if (isEmpty(value)) {
                            return createError({
                                path: PATH,
                                message: t(
                                    "FieldBuilder_Error_DataSheet_Grouping_Required"
                                ),
                            });
                        } else {
                            return true;
                        }
                    },
                })
        );

        return {
            dataSheetAtom,
            groupingAtom,
        };
    }, [fieldTypeAtom, dataSheetAssignments]);
}

export function getRowGrouping(
    groupingColumns: DataSheetColumnView[],
    groupingAndControls: DataSheetGroupingAndControlsAssigned
): Array<DataSheetCellValue | null> {
    return groupingColumns.map((col) => groupingAndControls.columns[col.id]);
}

export function isGroupingEqual(
    grouping: (DataSheetCellValue | null)[],
    rowGrouping: (DataSheetCellValue | null)[]
) {
    return isEqual(grouping, rowGrouping);
}

type ExtendedDataSheetGroupingAndControlsAssigned =
    DataSheetGroupingAndControlsAssignedForCurrentControl & {
        noLongerExists?: true;
    };

const DataSheetEditorInner = <FT extends DataSheetFieldTypeDetails>(
    {
        fieldId,
        atom: fieldTypeAtom,
        dataSheetAssignments,
        controlOverrideId,
        controlOverrideTitle,
        disabled = false,
    }: DataSheetEditorProps<FT>,
    ref: CompoundFormForwardedRef
) => {
    const { dataSheetAtom, groupingAtom } =
        useFieldTypeAtomDataSheetAndGrouping(
            fieldTypeAtom,
            dataSheetAssignments
        );

    const allAtoms = useMemo(() => {
        return {
            dataSheetAtom,
            groupingAtom,
        };
    }, [dataSheetAtom, groupingAtom]);
    const { validate: validateForm } = useCompoundFormState(
        allAtoms,
        dataSheetAtom
    );
    useImperativeHandle(ref, () => {
        return {
            validate(force) {
                return validateForm(force);
            },
        };
    }, [validateForm]);

    const stringCompare = React.useMemo(() => Cmp.string(), []);

    const { dataSheetById, dataSheetOptions } = useMemo(() => {
        const dataSheetById: Record<string, DataSheetStructure> = {};
        dataSheetAssignments.linkedResources.dataSheets.forEach(
            (ds) => (dataSheetById[ds.id] = ds)
        );
        const dataSheetOptions = dataSheetAssignments.content
            .map((ds) => {
                return {
                    label: dataSheetById[ds.resource.dataSheetId].name,
                    value: ds.resource.dataSheetId,
                };
            })
            .toSorted(Cmp.map((option) => option.label, stringCompare));
        return {
            dataSheetById,
            dataSheetOptions,
        };
    }, [dataSheetAssignments, stringCompare]);

    const { controlById } = useMemo(() => {
        const controlById: Record<string, ControlOverview> = {};
        dataSheetAssignments.linkedResources.controls.forEach(
            (c) => (controlById[c.id] = c)
        );
        return {
            controlById,
        };
    }, [dataSheetAssignments]);

    const [dataSheetId] = useAtom(dataSheetAtom);
    const { value: grouping, error: groupingError } = useFormAtom(groupingAtom);

    const selectedDataSheetAssignments = dataSheetAssignments.content.find(
        (dsa) => dsa.resource.dataSheetId === dataSheetId
    );
    const selectedDataSheet = dataSheetById[dataSheetId];

    const {
        allDataSheetGroupings,
        selectedGroupingExists,
    }: {
        allDataSheetGroupings: ExtendedDataSheetGroupingAndControlsAssigned[];
        selectedGroupingExists: boolean;
    } = useMemo(() => {
        if (!selectedDataSheetAssignments) {
            return {
                allDataSheetGroupings: [],
                selectedGroupingExists: true,
            };
        }
        const groupingColumns = selectedDataSheet.columns.filter(
            (col) => col.groupingColumn
        );
        // Don't display an empty grouping, it is an error in the database
        if (isEmpty(grouping)) {
            return {
                allDataSheetGroupings:
                    selectedDataSheetAssignments.resource.groupings,
                selectedGroupingExists: true,
            };
        } else {
            const selectedGroupingExists: boolean = selectedDataSheetAssignments
                ? selectedDataSheetAssignments.resource.groupings.some(
                      (groupingAndControls) =>
                          isGroupingEqual(
                              grouping,
                              getRowGrouping(
                                  groupingColumns,
                                  groupingAndControls
                              )
                          )
                  )
                : false;
            if (selectedGroupingExists) {
                return {
                    allDataSheetGroupings:
                        selectedDataSheetAssignments.resource.groupings,
                    selectedGroupingExists,
                };
            } else {
                const notFound: ExtendedDataSheetGroupingAndControlsAssigned = {
                    columns: Object.fromEntries(
                        groupingColumns.map((col, index) => [
                            col.id,
                            grouping[index],
                        ])
                    ),
                    assignedToControls: controlOverrideId
                        ? {
                              [controlOverrideId]: [
                                  /* not found thus no field */
                              ],
                          }
                        : {},
                    currentControlFieldIds: [],
                    noLongerExists: true,
                };
                return {
                    allDataSheetGroupings: [notFound].concat(
                        selectedDataSheetAssignments.resource.groupings
                    ),
                    selectedGroupingExists,
                };
            }
        }
    }, [
        grouping,
        selectedDataSheetAssignments,
        selectedDataSheet,
        controlOverrideId,
    ]);

    const [groupingModal, setGroupingModal] =
        useState<DataSheetGroupingModalState | null>(null);

    return (
        <>
            <div className={theme.fieldDataSheetSelect}>
                <FASelectInput
                    label={t("FieldBuilder_Drawer_DataSheet_Select")}
                    atom={dataSheetAtom}
                    options={dataSheetOptions}
                    disabled={disabled}
                />
            </div>
            {dataSheetId && !isEmpty(dataSheetId) && (
                <FormBox>
                    <FormField>
                        <FormLabel>
                            {t("FieldBuilder_Drawer_DataSheet_Grouping")}
                        </FormLabel>
                        {(() => {
                            const content =
                                isEmpty(grouping) ||
                                grouping
                                    .flatMap((col, index) => [
                                        col !== null ? (
                                            <span key={`val-${index}`}>
                                                {col}
                                            </span>
                                        ) : (
                                            <i key={`val-${index}`}>
                                                {t("Empty")}
                                            </i>
                                        ),
                                        index + 1 < grouping.length ? (
                                            <span key={`sep-${index}`}>
                                                {", "}
                                            </span>
                                        ) : null,
                                    ])
                                    .filter((n) => n !== null);
                            if (selectedGroupingExists) {
                                return <div>{content}</div>;
                            } else {
                                return (
                                    <div>
                                        {content}&nbsp;
                                        <Tooltip
                                            title={t(
                                                "FieldBuilder_Error_DataSheet_Grouping_DoesNotExist"
                                            )}
                                        >
                                            <span>
                                                <Svg
                                                    src={alertTriangle}
                                                    className={
                                                        theme.fieldDataSheetGroupingDoesNotExistIcon
                                                    }
                                                />
                                            </span>
                                        </Tooltip>
                                    </div>
                                );
                            }
                        })()}
                        <FieldError error={groupingError?.value} />
                        {!disabled && (
                            <Button
                                id="select-datasheet-grouping"
                                variant="link"
                                className={
                                    theme.fieldDataSheetSelectGroupingButton
                                }
                                onClick={() =>
                                    setGroupingModal({
                                        groupingAtom,
                                    })
                                }
                            >
                                {isEmpty(grouping)
                                    ? t(
                                          "FieldBuilder_Drawer_DataSheet_Select_Grouping"
                                      )
                                    : t(
                                          "FieldBuilder_Drawer_DataSheet_Change_Grouping"
                                      )}
                            </Button>
                        )}
                    </FormField>
                </FormBox>
            )}
            {!disabled &&
                groupingModal &&
                selectedDataSheet &&
                selectedDataSheetAssignments && (
                    <DataSheetGroupingModal
                        fieldId={fieldId}
                        atom={groupingModal.groupingAtom}
                        dataSheet={selectedDataSheet}
                        dataSheetGroupings={allDataSheetGroupings}
                        controlById={controlById}
                        controlOverrideId={controlOverrideId}
                        controlOverrideTitle={controlOverrideTitle}
                        onClose={() => setGroupingModal(null)}
                    />
                )}
        </>
    );
};

interface DataSheetGroupingModalState {
    groupingAtom: FormAtom<(DataSheetCellValue | null)[]>;
}

export const DataSheetEditor = React.forwardRef(DataSheetEditorInner);

const columnHelper =
    createColumnHelper<ExtendedDataSheetGroupingAndControlsAssigned>();

const DataSheetGroupingModal = ({
    fieldId,
    atom: groupingAtom,
    dataSheet,
    dataSheetGroupings,
    controlById,
    controlOverrideId,
    controlOverrideTitle,
    onClose,
}: {
    fieldId: string;
    atom: FormAtom<(DataSheetCellValue | null)[]>;
    dataSheet: DataSheetStructure;
    dataSheetGroupings: ExtendedDataSheetGroupingAndControlsAssigned[];
    controlById: Record<string, ControlOverview>;
    controlOverrideId: string;
    controlOverrideTitle: string;
    onClose: () => void;
}) => {
    const { value: grouping, dispatchValue: dispatchGrouping } =
        useFormAtom(groupingAtom);

    interface SelectedGroupingState {
        grouping: (DataSheetCellValue | null)[];
        exists: "UNKNOWN" | "YES";
    }
    const [selectedGrouping, setSelectedGrouping] =
        useState<SelectedGroupingState>({
            grouping,
            // We don't know whether the selected grouping exists.
            // We don't need to know because we know the information on render.
            exists: "UNKNOWN",
        });

    const groupingColumns = dataSheet.columns.filter(
        (col) => col.groupingColumn
    );

    const stringCompare = React.useMemo(() => Cmp.string(), []);

    const columns = [
        columnHelper.display({
            id: "selection",
            size: TST_COLUMN_SIZE.CHECKBOX,
            meta: { fixedWidth: true },
            cell: (info) => {
                const groupingAndControls = info.row.original;
                const rowGrouping = getRowGrouping(
                    groupingColumns,
                    groupingAndControls
                );
                return (
                    <Radio
                        checked={
                            // Safer when the grouping that no longer exists has a different length.
                            // That should never happen, but we're never sure.
                            (groupingAndControls.noLongerExists &&
                                selectedGrouping.exists === "UNKNOWN") ||
                            // Regular case
                            (!groupingAndControls.noLongerExists &&
                                isGroupingEqual(
                                    selectedGrouping.grouping,
                                    rowGrouping
                                ))
                        }
                        disabled={groupingAndControls.noLongerExists}
                        onChange={
                            !groupingAndControls.noLongerExists
                                ? (event) => {
                                      if (event.target.checked) {
                                          setSelectedGrouping({
                                              grouping: rowGrouping,
                                              // Now we know, the grouping that no longer exists cannot be selected.
                                              exists: "YES",
                                          });
                                      }
                                  }
                                : undefined
                        }
                    />
                );
            },
        }),
        ...groupingColumns.map((col) =>
            columnHelper.display({
                id: col.id,
                header: () => col.name,
                size: TST_COLUMN_SIZE.L,
                cell: (info) => {
                    const groupingAndControls = info.row.original;
                    const value: DataSheetCellValue | null | undefined =
                        groupingAndControls.columns[col.id];
                    const content =
                        value !== null && value !== undefined ? (
                            value.toString()
                        ) : (
                            <i>{t("Empty")}</i>
                        );
                    if (groupingAndControls.noLongerExists) {
                        return (
                            <>
                                {content}&nbsp;
                                <Tooltip
                                    title={t(
                                        "FieldBuilder_Error_DataSheet_Grouping_DoesNotExist"
                                    )}
                                >
                                    <span>
                                        <Svg
                                            src={alertTriangle}
                                            className={
                                                theme.fieldDataSheetGroupingDoesNotExistIcon
                                            }
                                        />
                                    </span>
                                </Tooltip>
                            </>
                        );
                    } else {
                        return content;
                    }
                },
            })
        ),
        columnHelper.display({
            id: "controls",
            header: () =>
                t("FieldBuilder_Drawer_DataSheet_Modal_AssignedToControls"),
            size: TST_COLUMN_SIZE.L,
            cell: (info) => {
                const groupingAndControls = info.row.original;
                const assignedInAnyOtherFieldOfCurrentControl =
                    groupingAndControls.currentControlFieldIds.some(
                        (id) => id !== fieldId
                    );
                const titles = Object.keys(
                    groupingAndControls.assignedToControls
                )
                    // Remove current control, present in the backend payload if saved, absent if added after query
                    .filter((controlId) => controlId !== controlOverrideId)
                    // Re-add current control ONLY if it is used in another field
                    .concat(
                        assignedInAnyOtherFieldOfCurrentControl
                            ? [controlOverrideId]
                            : []
                    )
                    // Map to a tuple [title_undefined_if_empty, isCurrentControl]
                    .map((controlId): [string | undefined, boolean] => {
                        const isCurrentControl =
                            controlId === controlOverrideId;
                        if (isCurrentControl) {
                            if (!isEmpty(controlOverrideTitle)) {
                                return [controlOverrideTitle, isCurrentControl];
                            } else {
                                return [undefined, isCurrentControl];
                            }
                        } else {
                            return [
                                controlById[controlId].title,
                                isCurrentControl,
                            ];
                        }
                    });
                // Sort by title
                titles.sort(
                    Cmp.map(
                        (tuple) => tuple[0],
                        Cmp.undefinedLast(stringCompare)
                    )
                );
                // Format and intersperce commas
                const result: Array<React.ReactNode> = [];
                for (let i = 0; i < titles.length; ++i) {
                    const [title, isCurrentControl] = titles[i];
                    if (i > 0) {
                        result.push(<span key={`sep-${i}`}>, </span>);
                    }
                    const innerNode: React.ReactNode =
                        title !== undefined ? title : <i>{t("Empty")}</i>;
                    result.push(
                        <span key={`control-${i}`}>
                            {isCurrentControl ? <b>{innerNode}</b> : innerNode}
                        </span>
                    );
                }
                return result;
            },
        }),
    ];

    const tableRef = useRef<HTMLDivElement>(null);
    const table = useReactTable({
        data: dataSheetGroupings,
        columns,
        getRowId: (groupingAndControls) =>
            getRowGrouping(groupingColumns, groupingAndControls).join("@@@"),
        getCoreRowModel:
            getCoreRowModel<ExtendedDataSheetGroupingAndControlsAssigned>(),
    });

    return (
        <Modal
            variant="content"
            title={t("FieldBuilder_Drawer_DataSheet_Modal_Title")}
            open
            width="80%"
            footer={
                <>
                    <Button id="cancel" onClick={onClose}>
                        {t("Cancel")}
                    </Button>
                    <Button
                        id="ok"
                        variant="primary"
                        onClick={() => {
                            dispatchGrouping(selectedGrouping.grouping);
                            onClose();
                        }}
                    >
                        {t("Select")}
                    </Button>
                </>
            }
            onClose={onClose}
        >
            <div className={theme.fieldDataSheetGroupingTableScroll}>
                <TSTTable
                    tableRef={tableRef}
                    table={table}
                    noResultsFallback={t("NoResults")}
                    noSourceRowsFallback={t("NoResults")}
                />
            </div>
        </Modal>
    );
};
